<template>
  <v-navigation-drawer v-model="visible" app fixed temporary>
    <div class="nav-box">
      <v-list class="menu-list">
        <div v-for="menu in displayedMenus" :key="menu.id" class="menu-list-item">
          <v-list-item
            v-if="!menu.children || !menu.children.length"
            link
            class="menu-item"
            :to="menu.route"
          >
            <v-icon class="menu-icon">mdi-{{ menu.icon }}</v-icon>
            <v-list-item-title
              v-text="menu.title"
              class="menu-sub-item-title"
            ></v-list-item-title>
          </v-list-item>

          <v-list-group
            v-if="menu.children && menu.children.length"
            link
            class="menu-icon"
            :to="menu.route"
            :value="false"
          >
            <template v-slot:prependIcon>
              <v-icon class="menu-icon">mdi-{{ menu.icon }}</v-icon>
            </template>
            <template v-slot:activator>
              <v-list-item-title class="menu-item-title">{{
                menu.title
              }}</v-list-item-title>
            </template>

            <v-list-item
              v-for="child in menu.children"
              :key="child.id"
              :to="child.route"
              link
              class="menu-sub-item"
            >
              <v-list-item-title
                v-text="child.title"
                class="menu-sub-item-title"
              ></v-list-item-title>
            </v-list-item>
          </v-list-group>
        </div>
      </v-list>

      <div class="account-footer">
        <div class="account-sum-box" v-if="accountMenu.content">
          <div class="username-box">
            <v-icon class="menu-username-icon">mdi-account-circle</v-icon>
            <div class="username">{{ username }}</div>
          </div>

          <div class="user-status">{{ userStatus }}</div>
        </div>

        <v-list-item
          :to="accountMenu.route"
          @click="accountButtonClick()"
          link
          class="account-menu-item"
        >
          <v-icon class="account-menu-icon">mdi-{{ accountMenu.icon }}</v-icon>
          <v-list-item-title class="account-menu-title">{{
            accountMenu.title
          }}</v-list-item-title>
        </v-list-item>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<style scoped>
@import "../assets/styles/global-styles.css";
@import "../assets/styles/variables.css";

.nav-box {
  max-height: 100%;
}

.menu-list {
  overflow-y: scroll;
  min-height: 100vh;
}

.menu-icon {
  color: var(--dark-text-color) !important;
}

.menu-item-title {
  color: var(--dark-text-color);
  font-size: 1.2rem !important;
}

.menu-sub-item {
  padding: 8px 16px !important;
  min-height: unset !important;
}

.menu-sub-item-title {
  color: var(--dark-text-color);
  font-size: 1.2rem !important;
  padding-left: 20px;
}

.menu-list-item >>> .v-list-item__icon.v-list-group__header__prepend-icon {
  margin-right: 20px !important;
}

.menu-list-item >>> .v-list-item__icon.v-list-group__header__append-icon {
  min-width: 20px;
}

.account-footer {
  background: white;
  border-style: solid;
  border-width: 1px 0 0 0;
  border-color: var(--primary-separator-color);
  z-index: 8;
  width: 100%;
  position: sticky;
  bottom: 0;

  box-shadow: 0 -3px 6px -3px #2c2a4d96;
}

.account-sum-box {
  padding: 5px 20px;
}

.username-box {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.menu-username-icon {
  color: var(--dark-text-color) !important;
  margin-right: 10px;
}

.user-status {
  font-size: 0.9em;
  color: var(--details-text-color);
  padding-bottom: 5px;
}

.account-menu-item {
  background: var(--primary-background-color);
}

.account-menu-title {
  font-size: 1.2rem !important;
  color: white !important;
  padding-left: 20px;
}

.account-menu-icon {
  color: white !important;
}
</style>

<script lang="ts">
import { HeaderMenu } from "@/interfaces/HeaderMenu";
import { Rubric } from "@/interfaces/Rubric.interface";
import axios from "axios";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import * as UrlConsts from "@/scripts/UrlConsts";
import * as NavHeaderConsts from "@/scripts/NavHeaderConsts";
import { AuthenticationService } from "@/services/authentication.service";
import { Utils } from "@/scripts/Utils";
import Container from "./Container.component.vue";
import * as Consts from "@/scripts/Consts";
import {
  AccountMenuState,
  createBaseMenus,
  getAccountMenu,
  getDisplayedMenus,
  getUserStatusLabel,
} from "@/scripts/NavMenus";

const SEE_ALL_ARTICLES_ID = -1;
const SEE_ALL_PROJECTS_ID = -2;
const SEE_ALL_CLASSES_ID = -3;

/**
 * @desc menu de navigation pour l'affichage mobile
 */
@Component({
  components: {
    container: Container,
  },
})
export default class MobileNavigationMenu extends Vue {
  visible = false;

  username = "";
  userStatus = "";
  loggedIn = false;
  authStatus = 0;

  @Prop()
  menuVisible?: boolean;

  menus: Array<HeaderMenu> = createBaseMenus();

  private onAuthChange = () => this.syncAuth();

  mounted(): void {
    this.init();
    this.$root.$on("login", this.onAuthChange);
    this.$root.$on("logout", this.onAuthChange);
  }

  beforeDestroy(): void {
    this.$root.$off("login", this.onAuthChange);
    this.$root.$off("logout", this.onAuthChange);
  }

  /**
   * @desc affiche le menu de navigation
   */
  @Watch("menuVisible")
  showMenu(): void {
    this.visible = false;
    setTimeout(() => {
      this.visible = true;
    }, 10);
  }

  /**
   * @desc initialise le menu de navigation
   */
  private init(): void {
    this.syncAuth();
    this.loadArticles();
    this.loadUnarchivedRubrics();
  }

  get displayedMenus(): Array<HeaderMenu> {
    return getDisplayedMenus(this.menus, this.loggedIn, this.authStatus);
  }

  get accountMenu(): AccountMenuState {
    return getAccountMenu(this.loggedIn, "mobile");
  }

  private syncAuth(): void {
    this.loggedIn = AuthenticationService.isLoggedIn();
    this.authStatus = this.loggedIn
      ? AuthenticationService.getUserStatus()
      : 0;
    this.username = this.loggedIn ? AuthenticationService.getUsername() : "";
    this.userStatus = getUserStatusLabel(this.authStatus);
  }

  /**
   * @desc charge derniers articles publiés
   */
  private loadArticles(): void {
    const headers = AuthenticationService.getRequestHeader();
    const query =
      "?" +
      Utils.createUrlQuery({ limit: 5, startDate: new Date().toString() });

    axios
      .get(UrlConsts.getAllArticlesLimited + query, headers)
      .then((response) => {
        const articleMenus = [
          new HeaderMenu({
            title: "-- Voir tout --",
            id: SEE_ALL_ARTICLES_ID,
            route: `/articles`,
          }),
        ];
        for (const article of response.data) {
          articleMenus.push(
            new HeaderMenu({
              title: article.title,
              id: article.id,
              route: `/article/${article.id}`,
            }),
          );
        }
        this.getMenu(NavHeaderConsts.ARTICLES_MENU).children = articleMenus;
      })
      .catch((error) => console.log(error));
  }

  /**
   * @desc charge les rubriques non archivées
   */
  private loadUnarchivedRubrics(): void {
    const headers = AuthenticationService.getRequestHeader();

    axios
      .get(UrlConsts.getAllUnarchivedRubrics, headers)
      .then((response) => {
        const rubrics = response.data as Rubric[];
        const rubricMenus = [
          new HeaderMenu({
            title: "-- Voir tout --",
            id: SEE_ALL_PROJECTS_ID,
            route: `/projets-ecole`,
          }),
        ];
        const classMenus = [
          new HeaderMenu({
            title: "-- Voir tout --",
            id: SEE_ALL_CLASSES_ID,
            route: `/classes`,
          }),
        ];

        for (const rubric of rubrics) {
          const menu = new HeaderMenu({
            title: rubric.name,
            id: rubric.id,
            route: `/rubriques/articles/${rubric.id}`,
          });
          if (rubric.type === Consts.rubric_type_schoolProject) {
            rubricMenus.push(menu);
          } else if (rubric.type === Consts.rubric_type_classroom) {
            classMenus.push(menu);
          }
        }

        this.getMenu(NavHeaderConsts.PROJECTS_MENU).children = rubricMenus;
        this.getMenu(NavHeaderConsts.CLASSES_MENU).children = classMenus;
      })
      .catch((error) => console.log(error));
  }

  private getMenu(id: number): HeaderMenu {
    return this.menus.find((x) => x.id === id) || new HeaderMenu();
  }

  /**
   * @desc demande la déconnexion de l'utilisateur
   */
  accountButtonClick(): void {
    if (AuthenticationService.isLoggedIn()) {
      this.disconnect();
    }
  }

  /**
   * @description déconnecte l'utilisateur et l'envoie vers la page de connexion
   */
  disconnect(): void {
    AuthenticationService.logout(axios).then(() => {
      this.$root.$emit("logout");
      this.$router.push("/connexion").catch((err) => {
        console.log(err);
        return;
      });
    });
  }
}
</script>
